<template>
  <div>
    <h5 v-if="!hideHeaderText">{{ headerText }}</h5>
    <h6 class="text-uppercase">{{ title }}</h6>
    <b-spinner v-if="loading" class="text-center"></b-spinner>
    <div v-else>
      <div v-if="!$isCustomer || (isUnitAside && $isCustomer)">
        <div v-if="allowEdit" class="d-flex">
          <b-form-file
            v-model="attachmentFiles"
            multiple
            :accept="supportedTypesList.join(',')"
            placeholder="Choose a file..."
            drop-placeholder="Drop file here..."
            :file-name-formatter="formatNames"
            class="w-50"
          ></b-form-file>
          <span v-if="required" class="required-asterisk">*</span>
          <b-button variant="primary" class="ml-3" :disabled="uploadingFiles" @click="uploadAttachments()">
            <div v-show="uploadingFiles">
              <b-spinner small></b-spinner>
              Adding...
            </div>
            <div v-show="!uploadingFiles">Add</div>
          </b-button>
        </div>
        <div v-if="allowEdit" class="note">Size Limit 20MB</div>
        <div v-if="!isWarrantyAside && !$isCustomer" class="row col justify-content-end">
          <b-form-checkbox v-model="showRemoved" name="check-button" switch>Show Removed Attachments</b-form-checkbox>
        </div>
      </div>
      <b-overlay :show="loading" :opacity="0.6">
        <b-table
          class="align-middle border"
          striped
          sticky-header="90vh"
          no-border-collapse
          :items="attachments"
          :fields="fields"
          show-empty
          :empty-text="`There are no attachments for this ${label}`"
          :tbody-tr-class="grayOutIfRemoved"
        >
          <template #cell(index)="data">
            {{ data.index + 1 }}
          </template>

          <template #cell(originalFilename)="{ item }">
            <span v-if="item.dateRemoved" disabled>{{ item.originalFilename }}</span>
            <div v-else>
              <b-link @click="downloadFile(item)">
                {{ item.originalFilename }}
              </b-link>
              <font-awesome-icon
                v-if="isImageFileType(item)"
                :id="`attachment-${item.attachmentId}`"
                icon="image"
                class="ml-2"
                style="cursor: pointer"
              />
              <b-tooltip
                :target="`attachment-${item.attachmentId}`"
                triggers="hover"
                placement="auto"
                boundary="viewport"
                @show="() => fetchThumbnail(item)"
              >
                <div v-if="getThumbnailUrl(item.attachmentId)">
                  <img
                    :src="getThumbnailUrl(item.attachmentId)"
                    :alt="item.originalFilename"
                    style="max-width: auto; max-height: auto"
                    @error="handleImageError(item)"
                  />
                </div>
                <div v-else-if="thumbnailErrors[item.attachmentId]">
                  <font-awesome-icon icon="exclamation-triangle" />
                  Preview unavailable
                </div>
                <div v-else>
                  <b-spinner small></b-spinner>
                  Loading preview...
                </div>
              </b-tooltip>
            </div>
          </template>
          <template #cell(description)="{ item }">
            <b-input-group v-if="!$isCustomer && !item.dateRemoved && allowEdit && !item.fromCustomer">
              <b-form-input
                :id="`attachment-${item.attachmentId}`"
                :value="item.description"
                :class="
                  item.description && !hasFocus(`attachment-${item.attachmentId}`) ? 'form-control-plaintext' : ''
                "
                placeholder="Click to add description..."
                @input="updateDescription(item, $event)"
              ></b-form-input>
            </b-input-group>
            <b-input-group v-else-if="$isCustomer && item.fromCustomer">
              <b-form-input
                :id="`attachment-${item.attachmentId}`"
                :value="item.description"
                :class="
                  item.description && !hasFocus(`attachment-${item.attachmentId}`) ? 'form-control-plaintext' : ''
                "
                placeholder="add description..."
                @input="updateDescription(item, $event)"
              ></b-form-input>
            </b-input-group>
            <div v-else>{{ item.description }}</div>
          </template>

          <template #cell(info)="{ item }">
            <div>{{ item.dateCreate | date }}</div>
            <div v-if="item.employeeCreate" class="text-capitalize">
              {{ item.employeeCreate.name.toLowerCase() }}
            </div>
            <div v-if="item.vcpUserNameCreated" class="text-capitalize">
              {{ item.vcpUserNameCreated }}
            </div>
            <div v-if="!$isCustomer && item.vcpUserNameCreated" class="note">Customer</div>
          </template>

          <template #cell(internal)="{ item }">
            <div class="text-center">
              <b-form-checkbox
                :disabled="!allowEdit || item.dateRemoved != null || !allowInternal || item.fromCustomer"
                :checked="item.internal"
                @change="updateInternalFlag(item)"
              />
            </div>
          </template>

          <template #cell(dateRemoved)="{ item }">
            <div v-if="!item.dateRemoved" class="text-center">
              <b-button
                v-if="allowEdit"
                variant="secondary"
                size="sm"
                :disabled="!$isCustomer && item.fromCustomer"
                @click="confirmRemove(item)"
              >
                <font-awesome-icon icon="trash-alt" />
              </b-button>
            </div>
            <div v-else-if="!item.fromCustomer">
              <span>Removed</span>
              <div>{{ item.dateRemoved | date }}</div>
              <employee-display :employee="item.employeeRemoved" :show-email="false" />
            </div>
          </template>
        </b-table>
      </b-overlay>
      <b-button
        v-if="allowEdit && !isWarrantySubmissionPage && !isPartsRequestPage"
        variant="primary"
        :disabled="updatingAttachments"
        @click="updateAttachments()"
      >
        <div v-show="updatingAttachments">
          <b-spinner small></b-spinner>
          Updating...
        </div>
        <div v-show="!updatingAttachments">Update</div>
      </b-button>
      <warning-modal
        :id="`AttachmentDeleteWarning_${_uid}`"
        ref="DeleteWarning"
        warning-text="Are you sure you want to remove this attachment?"
        continue-btn-text="Yes, Remove"
      />
    </div>
  </div>
</template>

<script>
// Components
import WarningModal from '@/shared/components/WarningModal';
import EmployeeDisplayComponent from '@/shared/components/ui/EmployeeDisplayComponent';
// Vuex
import { AttachmentGetters, AttachmentActions, AttachmentMutations } from '@/shared/store/attachment/types';
import { LookupGetters } from '@/shared/store/lookup/types';
import { mapGetters, mapActions, mapMutations } from 'vuex';
// helpers
import SuccessService from '@/shared/services/SuccessService';
import ErrorService from '@/shared/services/ErrorService';
import ExportService from '@/shared/services/ExportService';
// mixins
import UserAccessMixin from '@/shared/mixins/UserAccessMixin';
import UploadMixin from '@/shared/mixins/UploadMixin';
import debounce from 'lodash/debounce';
export default {
  name: 'Attachments',
  components: {
    'warning-modal': WarningModal,
    'employee-display': EmployeeDisplayComponent
  },
  mixins: [UserAccessMixin, UploadMixin],
  props: {
    storeKey: {
      type: String(),
      default: null
    },
    hideHeaderText: {
      type: Boolean(),
      default: false
    },
    isWarrantyAside: {
      type: Boolean(),
      default: false
    },
    isPartsRequestPage: {
      type: Boolean(),
      default: false
    },
    isUnitAside: {
      type: Boolean(),
      default: false
    },
    required: {
      type: Boolean(),
      default: true
    }
  },
  data() {
    return {
      loading: false,
      uploadingFiles: false,
      attachmentFiles: null,
      showRemoved: false,
      updatingAttachments: false,
      activeTab: 0,
      customerAttachments: null,
      numOfCustomerAttachments: 0,
      thumbnailAbortControllers: new Map(), // Track active requests
      thumbnailErrors: {},
      preloadQueue: [],
      isPreloading: false,
      maxPreloadConcurrent: 2,
      imagePreviewFileTypes: ['jpg', 'jpeg', 'png', 'bmp', 'gif', 'webp']
    };
  },
  computed: {
    ...mapGetters([
      AttachmentGetters.GET_ATTACHMENTS,
      AttachmentGetters.GET_WARRANTY_ASIDE_ATTACHMENTS,
      AttachmentGetters.GET_ATTACHMENTS_BY_KEY,
      AttachmentGetters.GET_THUMBNAIL
    ]),
    ...mapGetters({
      supportedTypesList: LookupGetters.GET_SUPPORTED_TYPES_LIST
    }),
    selectedFiles() {
      if (!this.attachmentFiles) return '';
      return this.attachmentFiles.map(x => x.name).join(', ');
    },
    attachments() {
      let attachments = this.storeKey
        ? this[AttachmentGetters.GET_ATTACHMENTS_BY_KEY](this.storeKey)
        : this[AttachmentGetters.GET_ATTACHMENTS];

      if (this.$isCustomer) {
        attachments = attachments?.filter(attachment => !attachment.internal);
      }

      if (!this.showRemoved) {
        attachments = attachments?.filter(attachment => !attachment.dateRemoved);
      }

      if (this.isWarrantyAside) {
        let attachmentsWarrantyAside = this[AttachmentGetters.GET_WARRANTY_ASIDE_ATTACHMENTS];
        attachmentsWarrantyAside = attachmentsWarrantyAside?.filter(attachment => !attachment.dateRemoved);
        return attachmentsWarrantyAside;
      }

      return attachments;
    },
    fields() {
      const sharedOptions = { tdClass: 'align-middle', sortable: true };
      const fields = [
        { key: 'index', label: '#', tdClass: 'align-middle', thStyle: 'width: 20px' },
        { key: 'originalFilename', label: 'Name (hover for preview)', ...sharedOptions },
        { key: 'description', label: 'Description', ...sharedOptions },
        { key: 'info', label: 'Added', ...sharedOptions }
      ];
      if (!this.$isCustomer && !this.isWarrantySubmissionPage) {
        fields.push({ key: 'internal', label: 'Internal', ...sharedOptions });
      }
      if (this.$isCustomer && this.isUnitAside) {
        fields.push({ key: 'dateRemoved', label: 'Actions', ...sharedOptions });
      }
      if (!this.$isCustomer) {
        fields.push({ key: 'dateRemoved', label: 'Actions', ...sharedOptions });
      }
      return fields;
    },
    serviceOrderId() {
      return this.$route.params.serviceOrderId;
    },
    unitInspectionId() {
      return this.$route.params.unitInspectionId;
    },
    allowEdit() {
      if (this.readonly || this.isWarrantyAside) return false;
      if (this.serviceOrderId || this.unitInspectionId) {
        return this.branchIsAccessible;
      }
      return true;
    },
    allowInternal() {
      return !this.$route.params.customerId;
    }
  },
  watch: {
    parentRequestsUpdate: {
      async handler(UpdateRequested) {
        if (UpdateRequested) {
          await this.updateAttachments();
          this.resetRequestUpdateFlag();
        }
      },
      immediate: false
    }
  },
  async created() {
    await this.fetch();
  },
  beforeDestroy() {
    // Cancel all pending requests
    this.thumbnailAbortControllers.forEach(controller => controller.abort());
    this.thumbnailAbortControllers.clear();

    // Revoke all blob URLs to free memory
    const attachments = this[AttachmentGetters.GET_ATTACHMENTS] || [];
    attachments.forEach(item => {
      const url = this.getThumbnailUrl(item.attachmentId);
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
  },
  methods: {
    ...mapActions([
      AttachmentActions.FETCH_ATTACHMENTS,
      AttachmentActions.UPLOAD_ATTACHMENTS,
      AttachmentActions.DOWNLOAD_ATTACHMENT,
      AttachmentActions.UPDATE_ATTACHMENTS,
      AttachmentActions.REMOVE_ATTACHMENT,
      AttachmentActions.FETCH_THUMBNAIL
    ]),
    ...mapMutations([
      AttachmentMutations.SET_ATTACHMENT_DESCRIPTION,
      AttachmentMutations.SET_ATTACHMENT_INTERNAL_FLAG,
      AttachmentMutations.RESET_STATE
    ]),
    async fetch() {
      this.loading = true;
      try {
        await this[AttachmentActions.FETCH_ATTACHMENTS]({
          key: this.storeKey,
          route: this.path,
          isWarrantyAside: this.isWarrantyAside
        });

        // Start background preloading after attachments are loaded
        this.$nextTick(() => {
          this.startBackgroundPreload();
        });
      } catch {
        ErrorService.createErrorToast(this, 'Error loading Attachments.');
      } finally {
        this.loading = false;
      }
    },
    isImageFileType(item) {
      return this.imagePreviewFileTypes.some(fileType => item.originalFilename.toLowerCase().includes(fileType));
    },
    startBackgroundPreload() {
      // Get all attachments without thumbnails (not just first 10)
      const attachmentsToPreload = (this.attachments || []).filter(
        item => !item.dateRemoved && !this.getThumbnailUrl(item.attachmentId)
      );

      this.preloadQueue = attachmentsToPreload;
      this.processPreloadQueue();
    },

    async processPreloadQueue() {
      if (this.isPreloading || this.preloadQueue.length === 0) return;

      this.isPreloading = true;
      const batch = this.preloadQueue.splice(0, this.maxPreloadConcurrent);

      await Promise.allSettled(batch.map(item => this.preloadThumbnail(item)));

      this.isPreloading = false;

      // Continue processing if more items remain
      if (this.preloadQueue.length > 0) {
        setTimeout(() => this.processPreloadQueue(), 100);
      }
    },

    async preloadThumbnail(item) {
      // Skip if already loaded or being loaded
      if (this.getThumbnailUrl(item.attachmentId) || this.thumbnailAbortControllers.has(item.attachmentId)) {
        return;
      }

      const controller = new AbortController();
      this.thumbnailAbortControllers.set(item.attachmentId, controller);

      try {
        await this[AttachmentActions.FETCH_THUMBNAIL]({
          route: this.path,
          attachment: item,
          signal: controller.signal
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.$set(this.thumbnailErrors, item.attachmentId, true);
        }
      } finally {
        this.thumbnailAbortControllers.delete(item.attachmentId);
      }
    },

    fetchThumbnail: debounce(async function (item) {
      // Cancel any existing request for this attachment
      const existingController = this.thumbnailAbortControllers.get(item.attachmentId);
      if (existingController) {
        existingController.abort();
      }

      // Check if already cached
      if (this.getThumbnailUrl(item.attachmentId)) {
        return; // Already loaded
      }

      const controller = new AbortController();
      this.thumbnailAbortControllers.set(item.attachmentId, controller);

      try {
        await this[AttachmentActions.FETCH_THUMBNAIL]({
          route: this.path,
          attachment: item,
          signal: controller.signal
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.$set(this.thumbnailErrors, item.attachmentId, true);
        }
      } finally {
        this.thumbnailAbortControllers.delete(item.attachmentId);
      }
    }, 300), // 300ms delay before fetching on hover

    getThumbnailUrl(attachmentId) {
      const thumbnail = this[AttachmentGetters.GET_THUMBNAIL](attachmentId);

      // The thumbnail is already a blob URL string from the action
      return thumbnail || null;
    },
    async downloadFile(attachment) {
      try {
        const byteArray = await this[AttachmentActions.DOWNLOAD_ATTACHMENT]({
          attachment,
          route: this.path
        });
        ExportService.downloadByteArray(byteArray, attachment.originalFilename, false);
      } catch (error) {
        ErrorService.unknownExportError(this);
      }
    },
    async updateAttachments() {
      if (
        !this.isWarrantySubmissionPage &&
        !this.isPartsRequestPage &&
        (this.attachments == null || this.attachments.length == 0)
      ) {
        ErrorService.createErrorToast(this, 'Upload attachments before updating.');
        return;
      }
      try {
        this.updatingAttachments = this.loading = true;
        await this[AttachmentActions.UPDATE_ATTACHMENTS]({
          key: this.storeKey,
          attachments: this.attachments,
          route: this.path
        });
        if (!this.isWarrantySubmissionPage && !this.isPartsRequestPage) {
          SuccessService.createSuccessToast(this.$root, `Updated attachments successfully.`);
        }
      } catch (error) {
        let errorMessage = 'Failed to update attachments.';
        ErrorService.createErrorToast(this, errorMessage);
      } finally {
        this.updatingAttachments = this.loading = false;
      }
    },
    updateDescription(attachment, description) {
      this[AttachmentMutations.SET_ATTACHMENT_DESCRIPTION]({ ...attachment, description });
    },
    updateInternalFlag(attachment) {
      const newFlagValue = !attachment.internal;
      const payload = {
        attachment,
        newFlagValue
      };
      this[AttachmentMutations.SET_ATTACHMENT_INTERNAL_FLAG](payload);
    },
    resetRequestUpdateFlag() {
      this.$emit('toggleOffRequestUpdateFlag', true);
    },
    hasFocus(id) {
      return document.activeElement === document.getElementById(id);
    },
    confirmRemove(attachment) {
      this.$refs.DeleteWarning.show(this, this.removeAttachment.bind(this, attachment));
    },
    async uploadAttachments() {
      await this.uploadFiles(this.attachmentFiles, this.storeKey);
      this.attachmentFiles = null;
    },
    async removeAttachment(attachment) {
      try {
        this.updatingAttachments = this.loading = true;
        await this[AttachmentActions.REMOVE_ATTACHMENT]({ key: this.storeKey, attachment, route: this.path });
      } catch (error) {
        let errorMessage = `Error removing attachment ${attachment.originalFilename}.`;
        ErrorService.createErrorToast(this, errorMessage);
      } finally {
        this.updatingAttachments = this.loading = false;
      }
    },
    grayOutIfRemoved(item) {
      return item && item.dateRemoved ? 'text-muted font-italic' : '';
    },
    handleImageError(item) {
      this.$set(this.thumbnailErrors, item.attachmentId, true);
    }
  }
};
</script>
